/**
 * Narrative Engine LLM Service
 * 4-provider LLM service for narrative blueprint generation.
 * Follows the same pattern as RiffWriter's llmService.ts.
 */

import { Type, generateText as geminiGenerateText } from '@/lib/gemini';
import { generateText as anthropicGenerateText } from '@/lib/anthropic';
import type { NarrativeLLMResponse } from '@/types/narrative.types';
import { trackUsage } from '@/utils/costTracker';
import { openaiCompatFetch } from '../utils/openaiProxyFetch';
import {
  getProvider, getGeminiKey, getGeminiModel,
  getClaudeKey, getClaudeProxy, getClaudeModel,
  getOpenAIKey, getOpenAIModel, getXAIKey, getXAIModel,
} from './llmShared';

// ============================================
// JSON Schema Instruction (for non-Gemini providers)
// ============================================

const JSON_SCHEMA_INSTRUCTION = `\n\nRespond in JSON only, no markdown wrapping. Use this exact schema:
{
  "selectedArcId": string,
  "arcReasoning": string,
  "character": {
    "stance": string,
    "ageEra": string,
    "backstoryHook": string,
    "emotionalWound": string,
    "desire": string,
    "obstacle": string,
    "voiceTexture": string,
    "signaturePhrase": string,
    "speaksLike": string
  }
}`;

// ============================================
// Provider: Gemini
// ============================================

async function generateBlueprintWithGemini(prompt: string): Promise<NarrativeLLMResponse> {
  const apiKey = getGeminiKey();
  if (!apiKey) {
    throw new Error("Gemini API key not configured. Please set your API key in Settings.");
  }

  const model = getGeminiModel();

  console.log('[NarrativeEngine] Calling Gemini API with model:', model);

  const { text, inputTokens, outputTokens } = await geminiGenerateText({
    apiKey,
    model,
    prompt,
    temperature: 0.9,
    topP: 0.95,
    maxOutputTokens: 4000,
    responseSchema: {
      type: Type.OBJECT,
      properties: {
        selectedArcId: { type: Type.STRING },
        arcReasoning: { type: Type.STRING },
        character: {
          type: Type.OBJECT,
          properties: {
            stance: { type: Type.STRING },
            ageEra: { type: Type.STRING },
            backstoryHook: { type: Type.STRING },
            emotionalWound: { type: Type.STRING },
            desire: { type: Type.STRING },
            obstacle: { type: Type.STRING },
            voiceTexture: { type: Type.STRING },
            signaturePhrase: { type: Type.STRING },
            speaksLike: { type: Type.STRING }
          },
          required: ['stance', 'ageEra', 'backstoryHook', 'emotionalWound', 'desire', 'obstacle', 'voiceTexture', 'signaturePhrase', 'speaksLike']
        }
      },
      required: ['selectedArcId', 'arcReasoning', 'character']
    },
  });

  trackUsage({ provider: 'gemini', model, feature: 'narrative', inputTokens, outputTokens });

  console.log('[NarrativeEngine] Gemini response received:', { hasText: !!text, textLength: text?.length });

  if (!text) {
    console.error("[NarrativeEngine] Empty Gemini response");
    throw new Error("No response from Gemini. The API returned an empty response.");
  }

  try {
    return JSON.parse(text) as NarrativeLLMResponse;
  } catch (parseError: unknown) {
    console.error("[NarrativeEngine] Failed to parse Gemini response, attempting repair:", text.slice(0, 500));

    const jsonMatch = text.match(/\{[\s\S]*\}/);
    if (jsonMatch) {
      try {
        return JSON.parse(jsonMatch[0]) as NarrativeLLMResponse;
      } catch {
        // Fall through to truncation repair
      }
    }

    let repaired = text.trim();
    repaired = repaired.replace(/^```(?:json)?\s*/i, '').replace(/\s*```$/i, '');
    const openBraces = (repaired.match(/\{/g) || []).length;
    const closeBraces = (repaired.match(/\}/g) || []).length;
    const openBrackets = (repaired.match(/\[/g) || []).length;
    const closeBrackets = (repaired.match(/\]/g) || []).length;

    if (openBraces > closeBraces) {
      const lastChar = repaired.replace(/\s+$/, '').slice(-1);
      if (lastChar !== '"' && lastChar !== '}' && lastChar !== ']' && lastChar !== ',') {
        repaired += '"';
      }
      for (let i = 0; i < openBrackets - closeBrackets; i++) repaired += ']';
      for (let i = 0; i < openBraces - closeBraces; i++) repaired += '}';

      try {
        return JSON.parse(repaired) as NarrativeLLMResponse;
      } catch {
        // Last resort: couldn't repair
      }
    }

    const errMsg = parseError instanceof Error ? parseError.message : 'Unknown parse error';
    throw new Error(`Invalid JSON from Gemini: ${errMsg}`);
  }
}

async function generateTextWithGemini(prompt: string): Promise<string> {
  const apiKey = getGeminiKey();
  if (!apiKey) {
    throw new Error("Gemini API key not configured. Please set your API key in Settings.");
  }
  const model = getGeminiModel();

  console.log('[NarrativeEngine] Calling Gemini API (text) with model:', model);

  const { text, inputTokens, outputTokens } = await geminiGenerateText({
    apiKey, model, prompt,
    temperature: 0.9, topP: 0.95, maxOutputTokens: 1000,
  });

  trackUsage({ provider: 'gemini', model, feature: 'narrative', inputTokens, outputTokens });

  if (!text) {
    throw new Error("No response from Gemini. The API returned an empty response.");
  }
  return text.trim();
}

// ============================================
// Provider: Claude
// ============================================

async function generateBlueprintWithClaude(prompt: string): Promise<NarrativeLLMResponse> {
  const apiKey = getClaudeKey();
  if (!apiKey) throw new Error("Claude API key not configured. Please set your API key in Settings.");
  const model = getClaudeModel();

  console.log('[NarrativeEngine] Calling Claude API with model:', model);

  const { text: responseText, inputTokens, outputTokens } = await anthropicGenerateText({
    apiKey, baseURL: getClaudeProxy(), model,
    userPrompt: prompt + JSON_SCHEMA_INSTRUCTION,
    temperature: 0.9, maxTokens: 2000,
  });

  trackUsage({ provider: 'claude', model, feature: 'narrative', inputTokens, outputTokens });

  console.log('[NarrativeEngine] Claude response received:', { textLength: responseText.length });

  const jsonMatch = responseText.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    throw new Error("Failed to parse Claude response as JSON");
  }
  return JSON.parse(jsonMatch[0]) as NarrativeLLMResponse;
}

async function generateTextWithClaude(prompt: string): Promise<string> {
  const apiKey = getClaudeKey();
  if (!apiKey) throw new Error("Claude API key not configured. Please set your API key in Settings.");
  const model = getClaudeModel();

  console.log('[NarrativeEngine] Calling Claude API (text) with model:', model);

  const { text, inputTokens, outputTokens } = await anthropicGenerateText({
    apiKey, baseURL: getClaudeProxy(), model,
    userPrompt: prompt, temperature: 0.9, maxTokens: 500,
  });

  trackUsage({ provider: 'claude', model, feature: 'narrative', inputTokens, outputTokens });
  return text.trim();
}

// ============================================
// Provider: OpenAI
// ============================================

async function generateBlueprintWithOpenAI(prompt: string): Promise<NarrativeLLMResponse> {
  const apiKey = getOpenAIKey();
  if (!apiKey) {
    throw new Error("OpenAI API key not configured. Please set your API key in Settings.");
  }

  const model = getOpenAIModel();

  console.log('[NarrativeEngine] Calling OpenAI API with model:', model);

  const response = await openaiCompatFetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model,
      temperature: 0.9,
      max_tokens: 2000,
      response_format: { type: "json_object" },
      messages: [{
        role: 'user',
        content: prompt + JSON_SCHEMA_INSTRUCTION
      }]
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = (errorData as { error?: { message?: string } })?.error?.message || `HTTP ${response.status}`;
    throw new Error(`OpenAI API error: ${message}`);
  }

  const data = await response.json();

  if (data.usage) {
    trackUsage({ provider: 'openai', model: getOpenAIModel(), feature: 'narrative', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0 });
  }

  const responseText = data.choices?.[0]?.message?.content || '';

  console.log('[NarrativeEngine] OpenAI response received:', {
    model: data.model,
    usage: data.usage,
    textLength: responseText.length
  });

  if (!responseText) {
    throw new Error("No response from OpenAI. The API returned an empty response.");
  }

  try {
    return JSON.parse(responseText) as NarrativeLLMResponse;
  } catch (parseError: unknown) {
    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      const errMsg = parseError instanceof Error ? parseError.message : 'Unknown parse error';
      throw new Error(`Invalid JSON from OpenAI: ${errMsg}`);
    }
    return JSON.parse(jsonMatch[0]) as NarrativeLLMResponse;
  }
}

async function generateTextWithOpenAI(prompt: string): Promise<string> {
  const apiKey = getOpenAIKey();
  if (!apiKey) {
    throw new Error("OpenAI API key not configured. Please set your API key in Settings.");
  }

  const model = getOpenAIModel();

  console.log('[NarrativeEngine] Calling OpenAI API (text) with model:', model);

  const response = await openaiCompatFetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model,
      temperature: 0.9,
      max_tokens: 500,
      messages: [{
        role: 'user',
        content: prompt
      }]
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = (errorData as { error?: { message?: string } })?.error?.message || `HTTP ${response.status}`;
    throw new Error(`OpenAI API error: ${message}`);
  }

  const data = await response.json();

  if (data.usage) {
    trackUsage({ provider: 'openai', model: getOpenAIModel(), feature: 'narrative', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0 });
  }

  const responseText = data.choices?.[0]?.message?.content || '';

  if (!responseText) {
    throw new Error("No response from OpenAI. The API returned an empty response.");
  }

  return responseText.trim();
}

// ============================================
// Provider: xAI (Grok)
// ============================================

async function generateBlueprintWithXAI(prompt: string): Promise<NarrativeLLMResponse> {
  const apiKey = getXAIKey();
  if (!apiKey) {
    throw new Error("xAI API key not configured. Please set your API key in Settings.");
  }

  const model = getXAIModel();

  console.log('[NarrativeEngine] Calling xAI API with model:', model);

  const response = await fetch('https://api.x.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model,
      temperature: 0.9,
      max_tokens: 2000,
      response_format: { type: "json_object" },
      messages: [{
        role: 'user',
        content: prompt + JSON_SCHEMA_INSTRUCTION
      }]
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = (errorData as { error?: { message?: string } })?.error?.message || `HTTP ${response.status}`;
    throw new Error(`xAI API error: ${message}`);
  }

  const data = await response.json();

  if (data.usage) {
    const costTicks = data.usage.cost_in_usd_ticks;
    const actualCost = typeof costTicks === 'number' ? costTicks / 10_000_000_000 : undefined;
    trackUsage({ provider: 'xai', model: getXAIModel(), feature: 'narrative', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0, actualCost });
  }

  const responseText = data.choices?.[0]?.message?.content || '';

  console.log('[NarrativeEngine] xAI response received:', {
    model: data.model,
    usage: data.usage,
    textLength: responseText.length
  });

  if (!responseText) {
    throw new Error("No response from xAI. The API returned an empty response.");
  }

  try {
    return JSON.parse(responseText) as NarrativeLLMResponse;
  } catch (parseError: unknown) {
    const jsonMatch = responseText.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      const errMsg = parseError instanceof Error ? parseError.message : 'Unknown parse error';
      throw new Error(`Invalid JSON from xAI: ${errMsg}`);
    }
    return JSON.parse(jsonMatch[0]) as NarrativeLLMResponse;
  }
}

async function generateTextWithXAI(prompt: string): Promise<string> {
  const apiKey = getXAIKey();
  if (!apiKey) {
    throw new Error("xAI API key not configured. Please set your API key in Settings.");
  }

  const model = getXAIModel();

  console.log('[NarrativeEngine] Calling xAI API (text) with model:', model);

  const response = await fetch('https://api.x.ai/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify({
      model,
      temperature: 0.9,
      max_tokens: 500,
      messages: [{
        role: 'user',
        content: prompt
      }]
    })
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message = (errorData as { error?: { message?: string } })?.error?.message || `HTTP ${response.status}`;
    throw new Error(`xAI API error: ${message}`);
  }

  const data = await response.json();

  if (data.usage) {
    const costTicks = data.usage.cost_in_usd_ticks;
    const actualCost = typeof costTicks === 'number' ? costTicks / 10_000_000_000 : undefined;
    trackUsage({ provider: 'xai', model: getXAIModel(), feature: 'narrative', inputTokens: data.usage.prompt_tokens || 0, outputTokens: data.usage.completion_tokens || 0, actualCost });
  }

  const responseText = data.choices?.[0]?.message?.content || '';

  if (!responseText) {
    throw new Error("No response from xAI. The API returned an empty response.");
  }

  return responseText.trim();
}

// ============================================
// Exported Functions
// ============================================

/**
 * Generate a full narrative blueprint (arc selection + character) via the active LLM provider.
 * Routes to the user's configured provider and returns structured JSON.
 */
export async function generateNarrativeBlueprint(prompt: string): Promise<NarrativeLLMResponse> {
  try {
    const provider = getProvider();
    console.log(`[NarrativeEngine] Generating narrative blueprint via ${provider}`);

    switch (provider) {
      case 'xai':
        return await generateBlueprintWithXAI(prompt);
      case 'openai':
        return await generateBlueprintWithOpenAI(prompt);
      case 'claude':
        return await generateBlueprintWithClaude(prompt);
      case 'gemini':
      default:
        return await generateBlueprintWithGemini(prompt);
    }
  } catch (error: unknown) {
    const provider = getProvider();
    const errMsg = error instanceof Error ? error.message : 'Please verify API connectivity.';
    console.error(`[NarrativeEngine] Blueprint generation failed (${provider}):`, errMsg);
    throw new Error(`Narrative Engine failure (${provider}). ${errMsg}`);
  }
}

/**
 * Generate a single text field via the active LLM provider.
 * Used for per-field regeneration — lighter call, no JSON schema needed.
 * Returns raw text response.
 */
export async function generateSingleField(prompt: string): Promise<string> {
  try {
    const provider = getProvider();
    console.log(`[NarrativeEngine] Generating single field via ${provider}`);

    switch (provider) {
      case 'xai':
        return await generateTextWithXAI(prompt);
      case 'openai':
        return await generateTextWithOpenAI(prompt);
      case 'claude':
        return await generateTextWithClaude(prompt);
      case 'gemini':
      default:
        return await generateTextWithGemini(prompt);
    }
  } catch (error: unknown) {
    const provider = getProvider();
    const errMsg = error instanceof Error ? error.message : 'Please verify API connectivity.';
    console.error(`[NarrativeEngine] Single field generation failed (${provider}):`, errMsg);
    throw new Error(`Narrative Engine failure (${provider}). ${errMsg}`);
  }
}
